import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FileText, Search, Filter, RefreshCw, Eye, CheckCircle2, XCircle, Clock, AlertCircle, ChevronLeft, ChevronRight, DollarSign, Calendar, Loader2, Download, Trash2 } from 'lucide-react';
import { invoicesService } from '../services/invoicesService';
import { authService } from '../services/authService';
import { downloadCSV, downloadJSON } from '../utils/exportUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';

/**
 * Invoices List Page
 * Displays all invoices with filtering, sorting, and pagination
 */
const Invoices = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    per_page: 20,
    total_pages: 0,
  });
  const [filters, setFilters] = useState({
    status: 'all',
    account_id: 'all',
    search: '',
    start_date: '',
    end_date: '',
  });
  const [approvingId, setApprovingId] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  // Fetch invoices
  const fetchInvoices = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        per_page: pagination.per_page,
      };

      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }

      if (filters.account_id && filters.account_id !== 'all') {
        params.account_id = parseInt(filters.account_id);
      }

      if (filters.start_date) {
        params.start_date = filters.start_date;
      }

      if (filters.end_date) {
        params.end_date = filters.end_date;
      }

      if (filters.search) {
        params.search = filters.search;
      }

      const response = await invoicesService.list(params);

      setInvoices(response.data?.invoices || []);
      setPagination(response.data?.pagination || pagination);
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to load invoices';
      setError(errorMessage);
      console.error('Invoices fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await authService.getAccounts();
        setAccounts(response.data?.accounts || []);
      } catch (err) {
        console.error('Error fetching accounts:', err);
      }
    };
    fetchAccounts();
  }, []);

  // Track if component has mounted to avoid double-fetching
  const hasMountedRef = useRef(false);
  const prevPathRef = useRef(null);

  // Fetch invoices on mount and when filters change
  useEffect(() => {
    fetchInvoices(1);
    hasMountedRef.current = true;
    prevPathRef.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.account_id, filters.start_date, filters.end_date]);

  // Refetch when navigating to this page from another route
  useEffect(() => {
    if (hasMountedRef.current && location.pathname === '/finance/invoices' && prevPathRef.current !== '/finance/invoices') {
      fetchInvoices(pagination.page);
    }
    prevPathRef.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.search !== undefined) {
        fetchInvoices(pagination.page);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    fetchInvoices(newPage);
  };

  const handleApproveClick = (invoice) => {
    setSelectedInvoice(invoice);
    setShowApprovalDialog(true);
    setApprovalNotes('');
  };

  const handleApproveCancel = () => {
    setShowApprovalDialog(false);
    setSelectedInvoice(null);
    setApprovalNotes('');
  };

  const handleDownloadInvoice = (invoice, format) => {
    try {
      switch (format) {
        case 'csv':
          const csvData = [{
            'Invoice Number': invoice.invoice_number,
            'Invoice ID': invoice.invoice_id,
            'Amount': invoice.amount,
            'Status': invoice.status,
            'Payment Date': invoice.payment_date || '',
            'Approved At': invoice.approved_at || '',
          }];
          downloadCSV(csvData, `invoice-${invoice.invoice_number}`);
          break;

        case 'json':
          downloadJSON(invoice, `invoice-${invoice.invoice_number}`, {
            invoice_number: invoice.invoice_number,
            exported_at: new Date().toISOString(),
          });
          break;

        default:
          break;
      }
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to download invoice';
      alert(errorMessage);
      console.error('Download error:', err);
    }
  };

  const handleApprove = async () => {
    if (!selectedInvoice) return;

    if (approvalNotes.length > 1000) {
      alert('Approval notes must be 1000 characters or less');
      return;
    }

    try {
      setApprovingId(selectedInvoice.id);
      await invoicesService.approve(selectedInvoice.id, {
        approval_notes: approvalNotes.trim() || undefined
      });
      // Refresh the list
      await fetchInvoices(pagination.page);
      // Close dialog
      setShowApprovalDialog(false);
      setSelectedInvoice(null);
      setApprovalNotes('');
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to approve invoice';
      alert(errorMessage);
      console.error('Approve error:', err);
    } finally {
      setApprovingId(null);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'secondary', icon: Clock, label: 'Pending' },
      approved: { variant: 'default', icon: CheckCircle2, label: 'Approved' },
      processed: { variant: 'outline', icon: CheckCircle2, label: 'Processed' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  // Handle bulk selection
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedInvoices(new Set(invoices.map(inv => inv.id)));
    } else {
      setSelectedInvoices(new Set());
    }
  };

  const handleSelectInvoice = (invoiceId, checked) => {
    const newSelected = new Set(selectedInvoices);
    if (checked) {
      newSelected.add(invoiceId);
    } else {
      newSelected.delete(invoiceId);
    }
    setSelectedInvoices(newSelected);
  };

  const handleBulkApprove = async () => {
    if (selectedInvoices.size === 0) {
      alert('Please select at least one invoice to approve');
      return;
    }

    if (!window.confirm(`Are you sure you want to approve ${selectedInvoices.size} invoice(s)?`)) {
      return;
    }

    try {
      setBulkApproving(true);
      const invoiceIds = Array.from(selectedInvoices);
      const results = { success: 0, failed: 0 };

      for (const invoiceId of invoiceIds) {
        try {
          await invoicesService.approve(invoiceId);
          results.success++;
        } catch (err) {
          results.failed++;
          console.error(`Failed to approve invoice ${invoiceId}:`, err);
        }
      }

      // Refresh the list
      await fetchInvoices(pagination.page);
      setSelectedInvoices(new Set());

      if (results.failed > 0) {
        alert(`Approved ${results.success} invoice(s). ${results.failed} failed.`);
      } else {
        alert(`Successfully approved ${results.success} invoice(s).`);
      }
    } catch (err) {
      alert('Failed to approve invoices. Please try again.');
      console.error('Bulk approve error:', err);
    } finally {
      setBulkApproving(false);
    }
  };

  const handleBulkDownload = (format) => {
    if (selectedInvoices.size === 0) {
      alert('Please select at least one invoice to download');
      return;
    }

    const selectedInvoiceData = invoices.filter(inv => selectedInvoices.has(inv.id));

    try {
      switch (format) {
        case 'csv':
          const csvData = selectedInvoiceData.map(invoice => ({
            'Invoice Number': invoice.invoice_number,
            'Invoice ID': invoice.invoice_id,
            'Amount': invoice.amount,
            'Status': invoice.status,
            'Payment Date': invoice.payment_date || '',
            'Approved At': invoice.approved_at || '',
          }));
          downloadCSV(csvData, `invoices-bulk-${selectedInvoices.size}`);
          break;

        case 'json':
          downloadJSON(selectedInvoiceData, `invoices-bulk-${selectedInvoices.size}`, {
            count: selectedInvoices.size,
            exported_at: new Date().toISOString(),
          });
          break;

        default:
          break;
      }
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to download invoices';
      alert(errorMessage);
      console.error('Bulk download error:', err);
    }
  };

  // Table columns
  const columns = useMemo(() => [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={selectedInvoices.size > 0 && selectedInvoices.size === invoices.length}
          onCheckedChange={handleSelectAll}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => {
        const invoice = row.original;
        return (
          <Checkbox
            checked={selectedInvoices.has(invoice.id)}
            onCheckedChange={(checked) => handleSelectInvoice(invoice.id, checked)}
            aria-label={`Select invoice ${invoice.invoice_number}`}
          />
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'invoice_number',
      header: 'Invoice Number',
      cell: ({ row }) => {
        const invoice = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <div className="font-medium">{invoice.invoice_number}</div>
              {invoice.invoice_id && (
                <div className="text-sm text-muted-foreground">
                  ID: {invoice.invoice_id}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ getValue }) => {
        const amount = getValue();
        return (
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium">{formatCurrency(amount)}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue();
        return getStatusBadge(status);
      },
    },
    {
      accessorKey: 'payment_date',
      header: 'Payment Date',
      cell: ({ getValue }) => {
        const date = getValue();
        if (!date) return '-';
        return (
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            {new Date(date).toLocaleDateString()}
          </div>
        );
      },
    },
    {
      accessorKey: 'approved_at',
      header: 'Approved At',
      cell: ({ getValue }) => {
        const date = getValue();
        if (!date) return '-';
        return (
          <div className="text-sm">
            {new Date(date).toLocaleDateString()}
            <div className="text-muted-foreground">
              {new Date(date).toLocaleTimeString()}
            </div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const invoice = row.original;
        const isApproving = approvingId === invoice.id;
        const canApprove = invoice.status === 'pending';

        return (
          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="outline"
              size="sm"
            >
              <Link to={`/finance/invoices/${invoice.id}`}>
                <Eye className="w-4 h-4 mr-1" />
                View
              </Link>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleDownloadInvoice(invoice, 'csv')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Download as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownloadInvoice(invoice, 'json')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Download as JSON
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {canApprove && (
              <Button
                variant="default"
                size="sm"
                onClick={() => handleApproveClick(invoice)}
                disabled={isApproving}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Approve
              </Button>
            )}
          </div>
        );
      },
    },
  ], [approvingId, selectedInvoices, invoices]);

  const table = useReactTable({
    data: invoices,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: pagination.total_pages,
    state: {
      pagination: {
        pageIndex: pagination.page - 1,
        pageSize: pagination.per_page,
      },
    },
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      <div className="mb-2 sm:mb-3 lg:mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-0.5">Invoices</h1>
          <p className="text-muted-foreground">View and manage your invoices</p>
        </div>
        {selectedInvoices.size > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {selectedInvoices.size} selected
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={bulkApproving}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Selected
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleBulkDownload('csv')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Download as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkDownload('json')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Download as JSON
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="default"
              size="sm"
              onClick={handleBulkApprove}
              disabled={bulkApproving}
            >
              {bulkApproving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Approve Selected ({selectedInvoices.size})
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedInvoices(new Set())}
            >
              Clear Selection
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search invoices..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Status</label>
              <Select
                value={filters.status}
                onValueChange={(value) => handleFilterChange('status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="processed">Processed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Start Date</label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">End Date</label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => fetchInvoices(pagination.page)}
                className="w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error message */}
      {error && (
        <Card className="mb-2 sm:mb-3 lg:mb-4 border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading invoices...</div>
          ) : invoices.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-medium mb-2">No invoices found</p>
              <p className="text-muted-foreground">Invoices will appear here once synced</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id} className="border-b">
                        {headerGroup.headers.map(header => (
                          <th
                            key={header.id}
                            className="h-12 px-2 sm:px-4 text-left align-middle font-medium text-muted-foreground text-xs sm:text-sm"
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map(row => (
                      <tr
                        key={row.id}
                        className="border-b transition-colors hover:bg-muted/50"
                      >
                        {row.getVisibleCells().map(cell => (
                          <td
                            key={cell.id}
                            className="p-2 sm:p-4 align-middle text-sm"
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border-t gap-4">
                  <div className="text-sm text-muted-foreground text-center sm:text-left">
                    Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
                    {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
                    {pagination.total} invoices
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <div className="text-sm">
                      Page {pagination.page} of {pagination.total_pages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.total_pages}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Approval Dialog */}
      {showApprovalDialog && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm sm:text-base">Approve Invoice</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-4">
                  Are you sure you want to approve invoice <strong>{selectedInvoice.invoice_number}</strong> for{' '}
                  <strong>{formatCurrency(selectedInvoice.amount)}</strong>?
                </p>
              </div>
              <div>
                <Label htmlFor="approval-notes">Approval Notes (Optional)</Label>
                <textarea
                  id="approval-notes"
                  className="w-full mt-1.5 min-h-[100px] px-3 py-2 text-sm border rounded-md resize-none"
                  placeholder="Add any notes or reason for approval..."
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  maxLength={1000}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {approvalNotes.length}/1000 characters
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleApproveCancel}
                  disabled={approvingId === selectedInvoice.id}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleApprove}
                  disabled={approvingId === selectedInvoice.id}
                >
                  {approvingId === selectedInvoice.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Approve
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Invoices;
